// headMarkerNotifications.js - Utility to send head marker email notifications
// Checks preferences in Firestore before sending via Cloud Function

import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { getCurrentUserOrgId } from './orgs';

// Built from the project ID so a staging build calls staging's function, not live's.
export const CLOUD_FUNCTION_URL = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/sendMarkerNotification`;

// Request options for CLOUD_FUNCTION_URL. The function only accepts signed-in
// callers - it checks who they are and only emails people in their own
// tutor's organisation - so every call carries the user's ID token.
export const notificationRequest = async (body) => {
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body)
  };
};

// Notification type definitions
export const NOTIFICATION_TYPES = {
  COMMENT_SUGGESTION: 'comment_suggestion',
  MARKING_COMPLETE: 'marking_complete',
  SUBMISSION_UNCLAIMED: 'submission_unclaimed',
  UNCLAIMED_TOO_LONG: 'unclaimed_too_long',
  CLAIMED_TOO_LONG: 'claimed_too_long',
  NEW_REGISTRATION: 'new_registration',
  MISSED_DEADLINE: 'missed_deadline',
  LATE_SUBMISSION: 'late_submission',
  NEW_HOMEWORK_SUBMITTED: 'new_homework_submitted',
  MARKER_CLAIMS: 'marker_claims',
  MARKER_STAT_RESET: 'marker_stat_reset',
  SUBMISSION_FLAGGED_FOR_REVIEW: 'submission_flagged_for_review',
  ADDITIONAL_PAGES_PENDING: 'additional_pages_pending'
};

// Human-readable labels for notification types
export const NOTIFICATION_LABELS = {
  [NOTIFICATION_TYPES.COMMENT_SUGGESTION]: {
    label: 'Comment Bank Suggestion',
    description: 'A marker suggests a comment to be added to the comment bank'
  },
  [NOTIFICATION_TYPES.MARKING_COMPLETE]: {
    label: 'Marking Ready for Review',
    description: 'A marker has completed marking and the homework is awaiting your review'
  },
  [NOTIFICATION_TYPES.SUBMISSION_UNCLAIMED]: {
    label: 'Submission Unclaimed/Released',
    description: 'A submission has been released back to the available pool'
  },
  [NOTIFICATION_TYPES.UNCLAIMED_TOO_LONG]: {
    label: 'Unclaimed Too Long',
    description: 'A submission has been sitting unclaimed beyond the threshold (scheduled)'
  },
  [NOTIFICATION_TYPES.CLAIMED_TOO_LONG]: {
    label: 'Claimed But Unmarked Too Long',
    description: 'A marker has had a submission claimed but not completed beyond the threshold (scheduled)'
  },
  [NOTIFICATION_TYPES.NEW_REGISTRATION]: {
    label: 'New Student Registration',
    description: 'A new student has registered and is awaiting approval'
  },
  [NOTIFICATION_TYPES.MISSED_DEADLINE]: {
    label: 'Missed Deadline Summary',
    description: 'List of students who missed a homework deadline (sent after deadline passes)'
  },
  [NOTIFICATION_TYPES.LATE_SUBMISSION]: {
    label: 'Late Homework Submitted',
    description: 'A student has submitted homework after the deadline'
  },
  [NOTIFICATION_TYPES.NEW_HOMEWORK_SUBMITTED]: {
    label: 'New Homework Submitted',
    description: 'A student has submitted a new piece of homework'
  },
  [NOTIFICATION_TYPES.MARKER_CLAIMS]: {
    label: 'Marker Claims Submission',
    description: 'A marker has claimed a submission from the available pool'
  },
  [NOTIFICATION_TYPES.MARKER_STAT_RESET]: {
    label: 'Marker Resets Stats',
    description: 'A marker has reset their completion statistics'
  },
  [NOTIFICATION_TYPES.SUBMISSION_FLAGGED_FOR_REVIEW]: {
    label: 'Submission Flagged for Review',
    description: 'A marker has flagged a submission as an outlier for you to review'
  },
  [NOTIFICATION_TYPES.ADDITIONAL_PAGES_PENDING]: {
    label: 'Additional Pages Pending Review',
    description: 'A student uploaded more pages after their submission was already claimed/marked'
  }
};

// Phase groupings for the UI
export const NOTIFICATION_PHASES = {
  'Review & Marking': [
    NOTIFICATION_TYPES.MARKING_COMPLETE,
    NOTIFICATION_TYPES.COMMENT_SUGGESTION,
    NOTIFICATION_TYPES.MARKER_CLAIMS,
    NOTIFICATION_TYPES.SUBMISSION_UNCLAIMED,
    NOTIFICATION_TYPES.MARKER_STAT_RESET,
    NOTIFICATION_TYPES.SUBMISSION_FLAGGED_FOR_REVIEW,
    NOTIFICATION_TYPES.ADDITIONAL_PAGES_PENDING
  ],
  'Student Activity': [
    NOTIFICATION_TYPES.NEW_HOMEWORK_SUBMITTED,
    NOTIFICATION_TYPES.LATE_SUBMISSION,
    NOTIFICATION_TYPES.NEW_REGISTRATION
  ],
  'Scheduled Alerts': [
    NOTIFICATION_TYPES.UNCLAIMED_TOO_LONG,
    NOTIFICATION_TYPES.CLAIMED_TOO_LONG,
    NOTIFICATION_TYPES.MISSED_DEADLINE
  ]
};

/**
 * Get head marker notification preferences from Firestore
 */
export const getHeadMarkerNotificationPreferences = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const docRef = doc(db, 'orgs', resolvedOrgId, 'settings', 'headMarkerNotifications');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    }
    
    const defaults = {};
    Object.values(NOTIFICATION_TYPES).forEach(type => {
      defaults[type] = false;
    });
    return defaults;
  } catch (error) {
    console.error('Error loading head marker notification preferences:', error);
    return {};
  }
};

/**
 * Get the head marker's email from the notification settings doc
 */
const getHeadMarkerEmail = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const docRef = doc(db, 'orgs', resolvedOrgId, 'settings', 'headMarkerNotifications');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.headMarkerEmail) {
        return {
          email: data.headMarkerEmail,
          name: data.headMarkerName || 'Head Marker'
        };
      }
    }
    
    console.error('No head marker email found in notification settings.');
    return null;
  } catch (error) {
    console.error('Error getting head marker email:', error);
    return null;
  }
};

/**
 * Get the head marker's display name for student/marker-facing UI copy
 * (e.g. "contact <name> if this keeps happening"). Now that the platform
 * has more than one org, UI text must never hardcode a specific tutor's
 * name - falls back to a generic label if the org hasn't set one.
 */
export const getHeadMarkerName = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const docRef = doc(db, 'orgs', resolvedOrgId, 'settings', 'headMarkerNotifications');
    const docSnap = await getDoc(docRef);
    return (docSnap.exists() && docSnap.data().headMarkerName) || 'your tutor';
  } catch (error) {
    console.error('Error getting head marker name:', error);
    return 'your tutor';
  }
};

/**
 * Send a notification to the head marker (for use by markers/head marker).
 * Checks preferences client-side first - if disabled, does nothing.
 */
export const sendHeadMarkerNotification = async (type, data = {}, orgId) => {
  try {
    const preferences = await getHeadMarkerNotificationPreferences(orgId);

    if (!preferences[type]) {
      console.log(`Head marker notification '${type}' is disabled, skipping.`);
      return false;
    }

    const headMarker = await getHeadMarkerEmail(orgId);
    if (!headMarker) {
      console.error('No head marker email found');
      return false;
    }
    
    const response = await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
        type: 'head_marker_notification',
        notificationType: type,
        headMarkerEmail: headMarker.email,
        headMarkerName: headMarker.name,
        data: data
      }));
    
    if (!response.ok) {
      console.error('Notification API error:', await response.text());
      return false;
    }
    
    console.log(`Head marker notification '${type}' sent successfully.`);
    return true;
    
  } catch (error) {
    console.error('Failed to send head marker notification:', error);
    return false;
  }
};

/**
 * Send a notification directly - checks preferences and email from Firestore,
 * then calls the Cloud Function. Used by students and registration where
 * the caller may not have full Firestore permissions.
 */
export const sendDirectNotification = async (type, data = {}, orgId) => {
  try {
    const preferences = await getHeadMarkerNotificationPreferences(orgId);

    if (!preferences[type]) {
      console.log(`Head marker notification '${type}' is disabled, skipping.`);
      return false;
    }

    const headMarker = await getHeadMarkerEmail(orgId);
    if (!headMarker) {
      console.error('No head marker email found');
      return false;
    }
    
    const response = await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
        type: 'head_marker_notification',
        notificationType: type,
        headMarkerEmail: headMarker.email,
        headMarkerName: headMarker.name,
        data: data
      }));
    
    if (!response.ok) {
      console.error('Direct notification error:', await response.text());
      return false;
    }
    
    console.log(`Direct notification '${type}' sent.`);
    return true;
    
  } catch (error) {
    console.error('Failed to send direct notification:', error);
    return false;
  }
};